import { Link, useLocation, useNavigate } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import {
  LayoutDashboard, FileText, ClipboardCheck, Package, Bell, MessageSquare,
  Factory, Wrench, Users, ShieldCheck, BarChart3, LogOut, Library, ClipboardList,
  Globe, ChevronLeft, PlusCircle, Droplet,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";

const NAV_BY_ROLE = {
  factory_customer: [
    { to: "/app", label: "لوحة التحكم", icon: LayoutDashboard, end: true },
    { to: "/app/requests", label: "طلباتي", icon: FileText },
    { to: "/app/orders", label: "الطلبيات النشطة", icon: Package },
    { to: "/app/quotations", label: "عروض الأسعار", icon: ClipboardCheck },
    { to: "/app/part-library", label: "مكتبة القطع", icon: Library },
    { to: "/app/notifications", label: "الإشعارات", icon: Bell },
  ],
  workshop_partner: [
    { to: "/app", label: "لوحة التحكم", icon: LayoutDashboard, end: true },
    { to: "/app/rfqs", label: "طلبات عروض الأسعار", icon: ClipboardList },
    { to: "/app/quotations", label: "عروض الأسعار المقدمة", icon: FileText },
    { to: "/app/orders", label: "الأعمال النشطة", icon: Wrench },
    { to: "/app/notifications", label: "الإشعارات", icon: Bell },
  ],
  inspector: [
    { to: "/app", label: "لوحة التحكم", icon: LayoutDashboard, end: true },
    { to: "/app/inspections", label: "زياراتي", icon: ClipboardCheck },
    { to: "/app/quality-reports", label: "تقارير الجودة", icon: ShieldCheck },
    { to: "/app/notifications", label: "الإشعارات", icon: Bell },
  ],
  operations_employee: [
    { to: "/app", label: "لوحة التحكم", icon: LayoutDashboard, end: true },
    { to: "/app/requests", label: "الطلبات", icon: FileText },
    { to: "/app/workshops", label: "الورش", icon: Wrench },
    { to: "/app/orders", label: "الطلبيات", icon: Package },
    { to: "/app/notifications", label: "الإشعارات", icon: Bell },
  ],
  admin: [
    { to: "/app", label: "لوحة التحكم", icon: LayoutDashboard, end: true },
    { to: "/app/requests", label: "الطلبات", icon: FileText },
    { to: "/app/workshops", label: "الورش", icon: Wrench },
    { to: "/app/factories", label: "المصانع", icon: Factory },
    { to: "/app/orders", label: "الطلبيات", icon: Package },
    { to: "/app/users", label: "المستخدمون", icon: Users },
    { to: "/app/analytics", label: "التحليلات", icon: BarChart3 },
    { to: "/app/watermarks", label: "العلامة المائية", icon: Droplet },
    { to: "/app/notifications", label: "الإشعارات", icon: Bell },
  ],
};

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const { toggle, lang, t } = useI18n();
  const loc = useLocation();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const items = NAV_BY_ROLE[user?.role] || [];

  const isActive = (item) => {
    if (item.end) return loc.pathname === item.to;
    return loc.pathname === item.to || loc.pathname.startsWith(item.to + "/");
  };

  const handleLogout = async () => {
    await logout();
    nav("/");
  };

  const roleLabel = {
    factory_customer: "عميل مصنع",
    workshop_partner: "شريك ورشة",
    inspector: "مفتش",
    operations_employee: "موظف عمليات",
    admin: "مدير النظام",
  }[user?.role] || user?.role;

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="dashboard-layout">
      {/* Sidebar */}
      <aside className={`fixed lg:sticky top-0 h-screen w-64 bg-brand-navy text-white flex-col z-30 transition-transform ${mobileOpen ? "flex" : "hidden lg:flex"}`}>
        <div className="px-6 py-5 border-b border-white/10">
          <Link to="/" className="flex items-center gap-2" data-testid="sidebar-brand">
            <div className="w-9 h-9 rounded-md bg-white/10 flex items-center justify-center">
              <Factory className="w-5 h-5 text-brand-amber" />
            </div>
            <div>
              <div className="font-bold text-white">MERQAV</div>
              <div className="text-[11px] text-slate-400">MERQAV</div>
            </div>
          </Link>
        </div>

        <div className="px-4 py-3 border-b border-white/10">
          <div className="text-xs text-slate-400">{roleLabel}</div>
          <div className="text-sm font-semibold text-white truncate">{user?.name}</div>
        </div>

        {user?.role === "factory_customer" && (
          <div className="px-4 pt-4">
            <Link to="/app/new-request" data-testid="sidebar-new-request-btn">
              <Button className="btn-cta w-full">
                <PlusCircle className="w-4 h-4 me-2" /> طلب تصنيع قطعة
              </Button>
            </Link>
          </div>
        )}

        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          {items.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              onClick={() => setMobileOpen(false)}
              data-testid={`sidebar-nav-${item.to.split("/").pop() || "home"}`}
              className={`sidenav-item ${isActive(item) ? "active" : ""}`}
            >
              <item.icon className="w-4 h-4" />
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="p-3 border-t border-white/10 space-y-1">
          <button onClick={toggle} className="sidenav-item w-full" data-testid="sidebar-lang-toggle">
            <Globe className="w-4 h-4" />
            <span>{lang === "ar" ? "English" : "العربية"}</span>
          </button>
          <button onClick={handleLogout} className="sidenav-item w-full text-red-300 hover:text-red-200" data-testid="sidebar-logout-btn">
            <LogOut className="w-4 h-4" />
            <span>تسجيل الخروج</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 min-w-0">
        <header className="lg:hidden bg-white border-b sticky top-0 z-20 px-4 py-3 flex items-center justify-between">
          <button onClick={() => setMobileOpen(!mobileOpen)} className="p-2" data-testid="mobile-sidebar-toggle">
            <ChevronLeft className="w-5 h-5 rtl-flip" />
          </button>
          <div className="font-bold text-brand-navy">MERQAV</div>
          <div className="w-9" />
        </header>
        <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto w-full" data-testid="dashboard-content">
          {children}
        </div>
      </div>

      {mobileOpen && <div className="fixed inset-0 bg-black/50 z-20 lg:hidden" onClick={() => setMobileOpen(false)} />}
    </div>
  );
}
